import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { ApoioDoMes, DiscordanciaParaGravar } from '../../types/apoioDaIa'
import type { SituacaoDaIa } from '../../types/chamado'

/**
 * A leitura automática do fechamento do mês.
 *
 * As chaves ficam aqui, junto dos leitores e dos escritores desta área, pela mesma razão de as
 * chaves de chamado morarem em `queries/chamados.ts`: o que `queries/chaves.ts` existe para evitar
 * é a divergência entre quem consulta e quem invalida quando os dois estão em arquivos diferentes.
 */
export const chavesDoApoioDoMes = {
  /** Tudo do apoio de IA ao fechamento. Invalidar isto derruba as consultas abaixo. */
  tudo: () => ['apoio-da-ia'] as const,
  doMes: (competencia: string) => ['apoio-da-ia', competencia] as const,
  situacao: () => ['apoio-da-ia', 'situacao'] as const,
}

/**
 * O apoio da IA para este mês — o parecer guardado e em que pé está a leitura.
 *
 * **Este GET pode agendar uma leitura no servidor**, e por isso a consulta é refeita a cada 4
 * segundos *enquanto* o estado for `PREPARANDO`, e só enquanto. Um `refetchInterval` fixo
 * transformaria a tela aberta num relógio batendo no servidor a vida inteira; sem nenhum, o parecer
 * que ficou pronto só apareceria se a pessoa recarregasse a página — e ela não tem por que saber
 * que precisa.
 *
 * **A tela nunca espera por ele.** O fechamento não depende desta consulta: os indicadores vêm de
 * outra, os atos da competência de outra, e nenhum dos dois é bloqueado enquanto isto carrega.
 *
 * @param competencia mês de referência no formato `AAAA-MM`; vazio não consulta nada
 * @returns a consulta do apoio
 */
export function useApoioDoMes(competencia: string) {
  return useQuery({
    queryKey: chavesDoApoioDoMes.doMes(competencia),
    queryFn: async () =>
      (await api.get<ApoioDoMes>(`/fiscalizacao/${competencia}/apoio-da-ia`)).data,
    enabled: competencia.length > 0,
    refetchInterval: (consulta) => (consulta.state.data?.estado === 'PREPARANDO' ? 4000 : false),
  })
}

/**
 * A leitura por IA está disponível neste ambiente?
 *
 * @param competencia mês de referência — é o prefixo da rota, e não muda a resposta
 * @returns a consulta da situação
 */
export function useSituacaoDaIaDoMes(competencia: string) {
  return useQuery({
    queryKey: chavesDoApoioDoMes.situacao(),
    queryFn: async () =>
      (await api.get<SituacaoDaIa>(`/fiscalizacao/${competencia}/apoio-da-ia/situacao`)).data,
    enabled: competencia.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Pede a leitura deste mês agora — o botão.
 *
 * Invalida só o apoio do mês: um parecer não muda apuração nenhuma, e é a Regra nº 3 aparecendo até
 * na invalidação de cache. O `timeout` é maior que o padrão porque modelo de raciocínio demora, e o
 * servidor espera até 90 segundos pela resposta.
 *
 * @param competencia mês de referência
 * @returns a mutação da leitura
 */
export function useGerarApoioDoMes(competencia: string) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<ApoioDoMes>(`/fiscalizacao/${competencia}/apoio-da-ia`, null, { timeout: 120_000 })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDoApoioDoMes.doMes(competencia) })
    },
  })
}

/**
 * Registra que a fiscalização discorda de um ponto do parecer.
 *
 * Invalida o apoio do mês para a marca aparecer ao lado do ponto refutado — a discordância não muda
 * o parecer nem o regenera, mas quem acabou de escrevê-la precisa vê-la registrada.
 *
 * @param competencia mês de referência
 * @returns a mutação da discordância
 */
export function useDiscordarDoParecer(competencia: string) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (discordancia: DiscordanciaParaGravar) => {
      await api.post(`/fiscalizacao/${competencia}/apoio-da-ia/discordancias`, discordancia)
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDoApoioDoMes.doMes(competencia) })
    },
  })
}
